import { defineStore } from 'pinia'
import ContatoService from '@/services/contato/ContatoService'

// Tipos para melhor tipagem
interface Contact {
  id: string
  given_name: string
  family_name: string
  middle_name?: string
  nickname?: string
  email_primary: string
  phone_primary: string
  company?: string
  department?: string
  position?: string
  birthday?: string
  note?: string
  cliente_id?: number | null
  is_cliente?: boolean
  cliente_summary?: {
    id: number
    slug?: string | null
    name: string
    tipo_pessoa?: string | null
  } | null
  is_merged: boolean
  merged_into?: string | null
  crm_company: number
  source: string
  external_id?: string
  created_at: string
  updated_at: string
  phones?: ContactPhone[]
  emails?: ContactEmail[]
  enderecos?: ContactEndereco[]
}

interface ContactPhone {
  id?: number
  label: string
  number: string
  is_primary: boolean
}

interface ContactEmail {
  id?: number
  label: string
  email: string
  is_primary: boolean
}

interface ContactEndereco {
  id?: number
  endereco: string
  nro: string
  complemento?: string
  bairro: string
  cep: string
  municipio: number
  city?: string // Nome da cidade (para exibição)
  state?: string // Sigla do estado (para exibição)
  tipo_endereco: string // Código do tipo (RES, COM, OUT)
  is_principal: boolean
}

interface ContactsState {
  contacts: Contact[]
  selectedContact: Contact | null
  timelineData: any | null
  isTimelineLoading: boolean
  inboxData: any | null
  isInboxLoading: boolean
  isLoading: boolean
  filters: {
    search: string
    includeMerged: boolean
  }
  pagination: {
    page: number
    pageSize: number
    totalPages: number
    totalCount: number
  }
}

export const useContactsStore = defineStore('contacts', {
  state: (): ContactsState => ({
    contacts: [],
    selectedContact: null,
    timelineData: null,
    isTimelineLoading: false,
    inboxData: null,
    isInboxLoading: false,
    isLoading: false,
    filters: {
      search: '',
      includeMerged: false,
    },
    pagination: {
      page: 1,
      pageSize: 20,
      totalPages: 0,
      totalCount: 0,
    },
  }),

  getters: {
    filteredContacts: (state) => {
      return state.contacts.filter((contact) => {
        let matches = true

        if (state.filters.search) {
          const search = state.filters.search.toLowerCase()
          const fullName = `${contact.given_name} ${contact.family_name}`.toLowerCase()
          const companyMatch = contact.company ? contact.company.toLowerCase().includes(search) : false
          const noteMatch = contact.note ? contact.note.toLowerCase().includes(search) : false

          matches =
            matches &&
            (fullName.includes(search) ||
              contact.email_primary.toLowerCase().includes(search) ||
              contact.phone_primary.includes(search) ||
              companyMatch ||
              noteMatch)
        }

        // Filtro para contatos mesclados
        if (!state.filters.includeMerged) {
          matches = matches && !contact.is_merged
        }

        return matches
      })
    },

    getContactById: (state) => {
      return (id: string) => state.contacts.find((contact) => contact.id === id)
    },

    getContactFullName: () => {
      return (contact: Contact) => {
        const parts = [contact.given_name, contact.middle_name, contact.family_name].filter(Boolean)
        return parts.join(' ')
      }
    },
  },

  actions: {
    async fetchContacts(resetPage = false) {
      if (resetPage) {
        this.pagination.page = 1
      }

      this.isLoading = true

      try {
        const params = new URLSearchParams()

        params.append('page', this.pagination.page.toString())
        params.append('page_size', this.pagination.pageSize.toString())

        if (this.filters.search) {
          params.append('search', this.filters.search)
        }

        if (this.filters.includeMerged) {
          params.append('include_merged', 'true')
        }

        const response = await ContatoService.getAll(`?${params.toString()}`)

        this.contacts = response.data.results || []
        this.pagination.totalPages = response.data.total_pages || 0
        this.pagination.totalCount = response.data.count || 0
      } catch (error) {
        console.error('Error fetching contacts:', error)
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async selectContact(contact: Contact) {
      this.selectedContact = contact

      // Buscar detalhes completos do contato se necessário
      try {
        const response = await ContatoService.get(contact.id)
        this.selectedContact = response.data
        // Buscar timeline associado ao contato (mensagens/leads/metrics)
        try {
          this.isTimelineLoading = true
          const tl = await ContatoService.getTimeline(contact.id)
          this.timelineData = tl.data
        } catch (err) {
          console.error('Error fetching contact timeline:', err)
          this.timelineData = null
        } finally {
          this.isTimelineLoading = false
        }
      } catch (error) {
        console.error('Error fetching contact details:', error)
      }
    },

    async fetchTimeline(contactId: string) {
      try {
        this.isTimelineLoading = true
        const response = await ContatoService.getTimeline(contactId)
        this.timelineData = response.data
        return response.data
      } catch (error) {
        console.error('Error fetching timeline:', error)
        this.timelineData = null
        throw error
      } finally {
        this.isTimelineLoading = false
      }
    },

    clearTimeline() {
      this.timelineData = null
      this.isTimelineLoading = false
    },

    async fetchInbox(
      contactId: string,
      params: {
        order?: 'asc' | 'desc'
        channel?: string
        direction?: 'incoming' | 'outgoing'
        lead_id?: number | string
        has_lead?: boolean | string | number
        limit?: number
      } = {},
    ) {
      try {
        this.isInboxLoading = true
        const response = await ContatoService.getInbox(contactId, params)
        this.inboxData = response.data
        return response.data
      } catch (error) {
        console.error('Error fetching inbox:', error)
        this.inboxData = null
        throw error
      } finally {
        this.isInboxLoading = false
      }
    },

    clearInbox() {
      this.inboxData = null
      this.isInboxLoading = false
    },

    async createContact(contactData: Partial<Contact>) {
      try {
        const response = await ContatoService.create(contactData)

        // Adicionar o novo contato à lista
        this.contacts.unshift(response.data)

        return response.data
      } catch (error) {
        console.error('Error creating contact:', error)
        throw error
      }
    },

    async updateContact(contactId: string, contactData: Partial<Contact>) {
      try {
        const response = await ContatoService.update(contactId, contactData)

        // Atualizar o contato na lista local
        const contactIndex = this.contacts.findIndex((contact) => contact.id === contactId)
        if (contactIndex !== -1) {
          this.contacts[contactIndex] = { ...this.contacts[contactIndex], ...response.data }
        }

        // Atualizar contato selecionado se necessário
        if (this.selectedContact && this.selectedContact.id === contactId) {
          this.selectedContact = { ...this.selectedContact, ...response.data }
        }

        return response.data
      } catch (error) {
        console.error('Error updating contact:', error)
        throw error
      }
    },

    async deleteContact(contactId: string) {
      try {
        await ContatoService.delete(contactId)

        // Remover o contato da lista local
        const contactIndex = this.contacts.findIndex((contact) => contact.id === contactId)
        if (contactIndex !== -1) {
          this.contacts.splice(contactIndex, 1)
        }

        // Limpar seleção se o contato deletado estava selecionado
        if (this.selectedContact && this.selectedContact.id === contactId) {
          this.selectedContact = null
        }
      } catch (error) {
        console.error('Error deleting contact:', error)
        throw error
      }
    },

    async mergeContacts(
      primaryContactId: string,
      mergeIds: string[],
      options: {
        fillMissingFields?: boolean
        preserveSecondaryNotes?: boolean
      } = {},
    ) {
      try {
        const response = await ContatoService.merge(primaryContactId, {
          merge_ids: mergeIds,
          fill_missing_fields: options.fillMissingFields || true,
          preserve_secondary_notes: options.preserveSecondaryNotes || true,
        })

        // Atualizar o contato primário na lista
        const primaryIndex = this.contacts.findIndex((contact) => contact.id === primaryContactId)
        if (primaryIndex !== -1) {
          this.contacts[primaryIndex] = response.data
        }

        // Marcar contatos secundários como mesclados
        mergeIds.forEach((mergeId) => {
          const mergedIndex = this.contacts.findIndex((contact) => contact.id === mergeId)
          if (mergedIndex !== -1) {
            this.contacts[mergedIndex] = {
              ...this.contacts[mergedIndex],
              is_merged: true,
              merged_into: primaryContactId,
            }
          }
        })

        return response.data
      } catch (error) {
        console.error('Error merging contacts:', error)
        throw error
      }
    },

    async transformToCliente(
      contactId: string,
      data: {
        cpf?: string
        cnpj?: string
        dados_complementares?: Record<string, any>
      } = {},
    ) {
      try {
        const response = await ContatoService.transformToCliente(contactId, data)
        return response.data
      } catch (error) {
        console.error('Error transforming contact to cliente:', error)
        throw error
      }
    },

    async getTransformPreview(contactId: string) {
      try {
        const response = await ContatoService.transformPreview(contactId)
        return response.data
      } catch (error) {
        console.error('Error getting transform preview:', error)
        throw error
      }
    },

    async importVCF(file: File) {
      try {
        const formData = new FormData()
        formData.append('file', file)

        const response = await ContatoService.importVCF(formData)

        // Recarregar contatos após importação
        await this.fetchContacts(true)

        return response.data
      } catch (error) {
        console.error('Error importing VCF:', error)
        throw error
      }
    },

    async getLeadsByContact(
      contactId: string,
      params: {
        search?: string
        page?: number
        page_size?: number
      } = {},
    ) {
      try {
        const response = await ContatoService.getLeadsByContato(contactId, params)
        return response.data
      } catch (error) {
        console.error('Error fetching leads by contact:', error)
        throw error
      }
    },

    // Ações para filtros
    setSearchFilter(search: string) {
      this.filters.search = search
    },

    setIncludeMergedFilter(includeMerged: boolean) {
      this.filters.includeMerged = includeMerged
    },

    clearFilters() {
      this.filters = {
        search: '',
        includeMerged: false,
      }
    },

    // Ações para paginação
    setPage(page: number) {
      this.pagination.page = page
    },

    setPageSize(pageSize: number) {
      this.pagination.pageSize = pageSize
      this.pagination.page = 1 // Reset to first page
    },

    // Limpar seleção
    clearSelectedContact() {
      this.selectedContact = null
    },
  },
})
